import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { alpha } from "@mui/material/styles";
import MediaPreview from "./MediaPreview";

export default function WizardMediaCarousel({
  items = [],
  mediaHeight = 250,
  onOpenLightbox,
  onRemoveItem,
  disableRemove = false,
  invalidMediaByClientId = {},
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mediaAspectRatios, setMediaAspectRatios] = useState({});
  const scrollerRef = useRef(null);
  const previousLengthRef = useRef(items.length);
  const previousFirstKeyRef = useRef(
    items[0]
      ? `${items[0]?.clientId || items[0]?.url || "media"}`
      : ""
  );

  useEffect(() => {
    setActiveIndex((prev) => {
      const maxIndex = Math.max(items.length - 1, 0);
      return Math.min(prev, maxIndex);
    });
  }, [items.length]);

  useEffect(() => {
    const nextFirstKey = items[0]
      ? `${items[0]?.clientId || items[0]?.url || "media"}`
      : "";
    const didPrependLikelyOccur =
      items.length > previousLengthRef.current &&
      Boolean(nextFirstKey) &&
      nextFirstKey !== previousFirstKeyRef.current;

    if (didPrependLikelyOccur) {
      setActiveIndex(0);
      if (scrollerRef.current) {
        scrollerRef.current.scrollTo({ left: 0, behavior: "smooth" });
      }
    }

    previousLengthRef.current = items.length;
    previousFirstKeyRef.current = nextFirstKey;
  }, [items]);

  if (!items.length) return null;

  return (
    <>
      <Box
        ref={scrollerRef}
        onScroll={(event) => {
          const el = event.currentTarget;
          if (!el?.children?.length) return;

          const viewportCenter = el.scrollLeft + el.clientWidth / 2;
          let closestIndex = 0;
          let closestDistance = Number.POSITIVE_INFINITY;

          Array.from(el.children).forEach((child, index) => {
            const childCenter = child.offsetLeft + child.clientWidth / 2;
            const distance = Math.abs(childCenter - viewportCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });

          setActiveIndex((prev) => (prev === closestIndex ? prev : closestIndex));
        }}
        sx={{
          display: "flex",
          overflowX: "auto",
          overflowY: "hidden",
          gap: 1,
          pb: 1,
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          touchAction: "pan-x pan-y",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {items.map((item, index) => {
          const cardKey = `${item?.clientId || item?.url || "media"}-${index}`;
          const cardAspectRatio = mediaAspectRatios[cardKey] || "4 / 3";
          const invalidMessage = String(
            invalidMediaByClientId?.[String(item?.clientId || "").trim()] || ""
          ).trim();

          return (
            <Box
              key={cardKey}
              sx={{
                flex: "0 0 auto",
                width: "auto",
                height: mediaHeight,
                aspectRatio: cardAspectRatio,
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                scrollSnapAlign: "start",
                border: "1px solid",
                borderColor: invalidMessage
                  ? "error.main"
                  : (index === activeIndex ? "primary.main" : "divider"),
                transition: "border-color 140ms ease",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.03)
                    : alpha(theme.palette.common.black, 0.02),
              }}
            >
              <Box
                onClick={() => {
                  if (typeof onOpenLightbox === "function") onOpenLightbox(item, index);
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  cursor: typeof onOpenLightbox === "function" ? "pointer" : "default",
                }}
              >
                <MediaPreview
                  mediaFile={item}
                  outerSx={{ m: 0, width: "100%", height: "100%" }}
                  borderRadius="0"
                  disableInteraction
                  fillContainer
                  onAspectRatioChange={(nextAspectRatio) => {
                    setMediaAspectRatios((prev) =>
                      prev[cardKey] === nextAspectRatio
                        ? prev
                        : { ...prev, [cardKey]: nextAspectRatio }
                    );
                  }}
                />
              </Box>

              {invalidMessage && (
                <Box
                  sx={(theme) => ({
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    backgroundColor: alpha(theme.palette.error.main, 0.68),
                    color: theme.palette.common.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 1.5,
                  })}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                    Video too long. Remove this file to continue.
                  </Typography>
                </Box>
              )}

              {typeof onRemoveItem === "function" && (
                <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 3 }}>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveItem(index, item);
                    }}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    onTouchStart={(event) => {
                      event.stopPropagation();
                    }}
                    disabled={disableRemove}
                    sx={(theme) => ({
                      width: 38,
                      height: 38,
                      borderRadius: "999px",
                      backgroundColor: theme.colors.red,
                      color: theme.colors.white,
                      "&:hover": { backgroundColor: theme.colors.darkBlue },
                    })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {items.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.8, mt: 0.25 }}>
          {items.map((_, index) => (
            <Box
              key={`dot-${index}`}
              sx={(theme) => ({
                width: index === activeIndex ? 18 : 8,
                height: 8,
                borderRadius: "999px",
                backgroundColor:
                  index === activeIndex ? theme.colors.blue : alpha(theme.colors.blue, 0.25),
                transition: "all 160ms ease",
              })}
            />
          ))}
          <Typography variant="caption" sx={{ ml: 0.8, opacity: 0.8 }}>
            {activeIndex + 1}/{items.length}
          </Typography>
        </Box>
      )}
    </>
  );
}
